import { useEffect, useRef, useState } from 'react';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { Typography } from 'Components/Typography';

import { moduleScrollingText } from 'assets/content/landingPages/supplements';

import { useScrollPosition } from 'utils/useScrollPosition';

const Root = styled.section<{
  img: { mobile: StaticImageData; tablet: StaticImageData; desktop: StaticImageData };
}>`
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100vw;
  height: 500px;

  background-image: ${({ img }) => `url(${img.mobile.src})`};
  background-size: cover;
  border-bottom: 2px solid ${theme.colors.primary[400]};

  overflow: hidden;

  ${legacyTheme.breakpoints.up('sm')} {
    background-image: ${({ img }) => `url(${img.tablet.src})`};
  }

  ${legacyTheme.breakpoints.up('md')} {
    background-image: ${({ img }) => `url(${img.desktop.src})`};
  }
`;

const Paragraph = styled(Typography)`
  font-size: 60px;
  font-weight: 300;
  line-height: normal;
  letter-spacing: normal;
  white-space: nowrap;
  color: ${theme.colors.neutral[100]};

  &:first-of-type {
    align-self: flex-start;
    margin-bottom: 13px;
    margin-left: 12%;
  }

  &:last-of-type {
    align-self: flex-end;
    margin-right: 12%;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 100px;

    &:first-of-type {
      margin-bottom: 2px;
      margin-left: 10%;
    }

    &:last-of-type {
      margin-right: 10%;
    }
  }

  ${legacyTheme.breakpoints.up('md')} {
    &:first-of-type {
      margin-bottom: 2px;
      margin-left: 6%;
    }

    &:last-of-type {
      margin-right: 6%;
    }
  }
`;

export const ModuleScrollingText = () => {
  const moduleScrollingTextRef = useRef<HTMLDivElement>(null);
  const moduleScrollingTextScroll = useScrollPosition(moduleScrollingTextRef);
  const [moduleScrollingTextTranslation, setModuleTranslation] = useState('0');

  // Watching for scroll
  useEffect(() => {
    /**
     * Compute a translation value based on the scroll movement
     * The animation will start only when we start seeing the module/block in the page
     */
    if (moduleScrollingTextScroll?.yPosition) {
      /**
       * Scroll in module starts when Y equals the viewport's height (min value)
       * It reaches its middle at 0 (mid value)
       * And ends when Y equals the opposite of the viewport's mid-height (max value)
       * From min to max, we have -1.5 * viewport's height
       */
      const moduleMaxScroll = -1.5 * window.innerHeight;

      /**
       * We keep 4 decimals to have more precision to gain fluidity in the movement
       * The movementPower will impact the importance of the translation
       */
      const scrollPositionModule = (
        ((moduleScrollingTextScroll.yPosition - window.innerHeight) / moduleMaxScroll) *
        10
      ).toFixed(4);

      setModuleTranslation(scrollPositionModule);
    }
  }, [moduleScrollingTextScroll]);

  return (
    <Root
      ref={moduleScrollingTextRef}
      data-testid="supplements-landing-module"
      img={moduleScrollingText.image}
    >
      {/* Inline-style animations are more performant than passing props to styled components (bigger heap size) */}
      <Paragraph
        markupName="p"
        style={{ transform: `translateX(${-moduleScrollingTextTranslation}%)` }}
        variant="p1"
      >
        {moduleScrollingText.paragraph}
      </Paragraph>
      <Paragraph
        markupName="p"
        style={{ transform: `translateX(${moduleScrollingTextTranslation}%)` }}
        variant="p1"
      >
        {moduleScrollingText.paragraph}
      </Paragraph>
    </Root>
  );
};
